// src/pages/FoodSearch.js — 음식검색: 왼쪽 필터 · 적용 조건 칩 · 줄 목록 · 오른쪽 상세(식단에 추가)
//   검색 조건은 전부 URL 쿼리에 둔다 (뒤로가기·새로고침·상단 검색창 공유)
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { foodsearch, foodsearchcount, foodsecd, getCodeSearch } from '../api/dietApi';
import { addFoodsToDay, mealByHour, todayStr } from '../api/dietDay';
import { isTokenValid } from '../components/RequireLogin';
import Drawer from '../components/ui/Drawer';
import Modal from '../components/ui/Modal';
import FoodRow from '../components/ui/FoodRow';
import Pagination from '../components/ui/Pagination';
import EmptyState from '../components/ui/EmptyState';
import { MacroText, fmt } from '../components/ui/Nutrients';
import { useToast } from '../components/ui/Toast';
import './FoodSearch.css';

const LIMIT = 10;
const MAIN = [{ code: 'D', label: '음식', key: '음식' }, { code: 'P', label: '가공식품', key: '가공식품' }];
// 서버 검색이 지원하는 영양소 범위 (100g 기준)
const RANGES = [
  { key: 'kcal', label: '칼로리', unit: 'kcal' },
  { key: 'carbo', label: '탄수화물', unit: 'g' },
  { key: 'protein', label: '단백질', unit: 'g' },
  { key: 'fat', label: '지방', unit: 'g' },
  { key: 'sugar', label: '당', unit: 'g' },
  { key: 'na', label: '나트륨', unit: 'mg' },
  { key: 'caffeine', label: '카페인', unit: 'mg' },
];
const DETAIL = [
  ['kcal', '칼로리', 'kcal'], ['carbo', '탄수화물', 'g'], ['protein', '단백질', 'g'], ['fat', '지방', 'g'],
  ['sugar', '당', 'g'], ['dietFiber', '식이섬유', 'g'], ['water', '수분', 'g'], ['na', '나트륨', 'mg'],
  ['choles', '콜레스테롤', 'mg'], ['satFat', '포화지방', 'g'], ['unsatFat', '불포화지방', 'g'], ['transFat', '트랜스지방', 'g'],
  ['allulose', '알룰로스', 'g'], ['erythritol', '에리스리톨', 'g'], ['glucose', '포도당', 'g'], ['caffeine', '카페인', 'mg'],
];
const FILTER_KEYS = ['mainCategory', 'subCategory', ...RANGES.flatMap((r) => [`${r.key}Min`, `${r.key}Max`])];

// URL → 서버 파라미터 (범위는 한쪽만 있으면 0 / 99999 로 채움)
const toApiParams = (sp) => {
  const p = {};
  const foodNm = sp.get('foodNm'); if (foodNm) p.foodNm = foodNm;
  ['mainCategory', 'subCategory'].forEach((k) => { if (sp.get(k)) p[k] = sp.get(k); });
  RANGES.forEach(({ key }) => {
    const min = sp.get(`${key}Min`); const max = sp.get(`${key}Max`);
    if (min || max) { p[`${key}Min`] = min || '0'; p[`${key}Max`] = max || '99999'; }
  });
  return p;
};

export default function FoodSearch() {
  const navigate = useNavigate();
  const toast = useToast();
  const [sp, setSp] = useSearchParams();
  const page = Math.max(1, parseInt(sp.get('page'), 10) || 1);
  const apiParams = useMemo(() => toApiParams(sp), [sp]);
  const hasCondition = Object.keys(apiParams).length > 0;

  const [secMap, setSecMap] = useState({});
  const [mealCodes, setMealCodes] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [keyword, setKeyword] = useState(sp.get('foodNm') || '');
  const [draft, setDraft] = useState({});
  const [filterOpen, setFilterOpen] = useState(false);   // 모바일 필터 시트
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    foodsecd().then((r) => setSecMap(r.data || {})).catch(() => {});
    getCodeSearch('dietCd').then((r) => setMealCodes(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  // 상단 검색창 등으로 URL 이 바뀌면 입력칸·필터 초안도 맞춘다
  useEffect(() => {
    setKeyword(sp.get('foodNm') || '');
    setDraft(Object.fromEntries(FILTER_KEYS.map((k) => [k, sp.get(k) || ''])));
  }, [sp]);

  useEffect(() => {
    if (!hasCondition) { setItems([]); setTotal(0); return undefined; }
    let ignore = false;
    setLoading(true); setError('');
    Promise.all([foodsearch({ ...apiParams, offset: (page - 1) * LIMIT, limit: LIMIT }), foodsearchcount(apiParams)])
      .then(([l, c]) => { if (!ignore) { setItems(Array.isArray(l.data) ? l.data : []); setTotal(Number(c.data) || 0); } })
      .catch(() => { if (!ignore) { setItems([]); setTotal(0); setError('검색 중 오류가 발생했습니다.'); } })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [apiParams, page, hasCondition]);

  const subName = useMemo(() => {
    const m = {};
    Object.values(secMap).flat().forEach((c) => { m[c.code] = c.codeNm; });
    return m;
  }, [secMap]);
  const catOf = (f) => subName[`${(f.foodCd || '')[0]}${f.fdSeCdMjr || ''}`] || '';

  const apply = (next) => {
    const n = new URLSearchParams();
    const merged = { foodNm: keyword.trim(), ...draft, ...next };
    Object.entries(merged).forEach(([k, v]) => { if (v !== '' && v != null && k !== 'page') n.set(k, v); });
    setSp(n);
    setFilterOpen(false);
  };

  const removeCond = (keys) => {
    const n = new URLSearchParams(sp);
    keys.forEach((k) => n.delete(k));
    n.delete('page');
    setSp(n);
  };

  const resetFilters = () => {
    const n = new URLSearchParams();
    if (sp.get('foodNm')) n.set('foodNm', sp.get('foodNm'));
    setSp(n);
    setFilterOpen(false);
  };

  // 적용된 조건 칩
  const chips = [];
  if (sp.get('mainCategory')) chips.push({ label: MAIN.find((m) => m.code === sp.get('mainCategory'))?.label, keys: ['mainCategory', 'subCategory'] });
  if (sp.get('subCategory')) chips.push({ label: subName[sp.get('subCategory')] || sp.get('subCategory'), keys: ['subCategory'] });
  RANGES.forEach(({ key, label, unit }) => {
    const min = sp.get(`${key}Min`); const max = sp.get(`${key}Max`);
    if (min || max) chips.push({ label: `${label} ${min || 0}~${max || ''}${unit}`, keys: [`${key}Min`, `${key}Max`] });
  });

  const filterCount = chips.length;
  const subOptions = draft.mainCategory ? (secMap[MAIN.find((m) => m.code === draft.mainCategory)?.key] || []) : [];

  const filters = (
    <div className="fs-filters">
      <div className="fs-group">
        <h3>분류</h3>
        <select className="select" value={draft.mainCategory || ''} onChange={(e) => setDraft((d) => ({ ...d, mainCategory: e.target.value, subCategory: '' }))}>
          <option value="">전체</option>
          {MAIN.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
        </select>
        <select className="select" value={draft.subCategory || ''} disabled={!draft.mainCategory} onChange={(e) => setDraft((d) => ({ ...d, subCategory: e.target.value }))}>
          <option value="">세부 분류 전체</option>
          {subOptions.map((c) => <option key={c.code} value={c.code}>{c.codeNm}</option>)}
        </select>
      </div>
      <div className="fs-group">
        <h3>영양소 <small>100g 기준</small></h3>
        {RANGES.map(({ key, label, unit }) => (
          <div key={key} className="fs-range">
            <span>{label}</span>
            <input className="input" type="number" min="0" placeholder="최소" value={draft[`${key}Min`] || ''} onChange={(e) => setDraft((d) => ({ ...d, [`${key}Min`]: e.target.value }))} />
            <span className="fs-tilde">~</span>
            <input className="input" type="number" min="0" placeholder="최대" value={draft[`${key}Max`] || ''} onChange={(e) => setDraft((d) => ({ ...d, [`${key}Max`]: e.target.value }))} />
            <span className="fs-unit">{unit}</span>
          </div>
        ))}
      </div>
      <div className="fs-filter-actions">
        <button className="btn ghost" type="button" onClick={resetFilters}>초기화</button>
        <button className="btn primary" type="button" onClick={() => apply({})}>적용</button>
      </div>
    </div>
  );

  let body;
  if (!hasCondition) body = <EmptyState message="음식 이름을 검색하거나 왼쪽 조건을 골라 보세요. (음식 16만 개)" />;
  else if (loading) body = <p className="fs-msg">검색 중…</p>;
  else if (error) body = <p className="fs-msg error">{error}</p>;
  else if (!items.length) body = <EmptyState message="조건에 맞는 음식이 없습니다." actionLabel={filterCount ? '조건 초기화' : undefined} onAction={resetFilters} />;
  else {
    body = (
      <>
        <div className="fs-list-head"><span>음식</span><span>칼로리</span><span>탄·단·지 (100g)</span></div>
        {items.map((f) => (
          <FoodRow key={f.foodCd} food={f} meta={catOf(f)} selected={detail?.foodCd === f.foodCd} onClick={() => setDetail(f)} />
        ))}
        <Pagination page={page} total={total} pageSize={LIMIT} onChange={(p) => { const n = new URLSearchParams(sp); n.set('page', p); setSp(n); window.scrollTo(0, 0); }} />
      </>
    );
  }

  return (
    <div className="fs">
      <form className="fs-search" onSubmit={(e) => { e.preventDefault(); apply({}); }}>
        <input className="input" value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="음식 이름 (예: 닭가슴살)" />
        <button className="btn primary" type="submit">검색</button>
        <button className="btn fs-filter-btn" type="button" onClick={() => setFilterOpen(true)}>필터{filterCount ? ` ${filterCount}` : ''}</button>
      </form>

      {chips.length > 0 && (
        <div className="fs-chips">
          {chips.map((c) => <button key={c.label} type="button" className="chip" onClick={() => removeCond(c.keys)}>{c.label} ✕</button>)}
          <button type="button" className="btn ghost" onClick={resetFilters}>조건 초기화</button>
        </div>
      )}

      <div className="fs-body">
        <aside className="fs-side">{filters}</aside>
        <section className="fs-results">
          {hasCondition && !loading && !error && <p className="fs-count">{total.toLocaleString('ko-KR')}건</p>}
          {body}
        </section>
      </div>

      <Modal open={filterOpen} title="검색 필터" size="sm" onClose={() => setFilterOpen(false)}>{filters}</Modal>

      <Drawer open={!!detail} title={detail?.foodNm || ''} onClose={() => setDetail(null)}>
        {detail && <FoodDetail food={detail} category={catOf(detail)} mealCodes={mealCodes} navigate={navigate} toast={toast} />}
      </Drawer>
    </div>
  );
}

function FoodDetail({ food, category, mealCodes, navigate, toast }) {
  const token = localStorage.getItem('token');
  const authed = !!token && isTokenValid(token);
  const [date, setDate] = useState(todayStr());
  const [meal, setMeal] = useState(mealByHour());
  const [intake, setIntake] = useState('100');
  const [saving, setSaving] = useState(false);
  const g = Number(intake);
  const ok = Number.isFinite(g) && g >= 1;
  const sc = (k) => ((Number(food[k]) || 0) * (ok ? g : 0)) / 100;

  const add = async () => {
    setSaving(true);
    try {
      await addFoodsToDay(date, meal, [{ foodCd: food.foodCd, intake: g }]);
      const mealNm = mealCodes.find((m) => m.code === meal)?.codeNm || meal;
      toast(`${food.foodNm} ${g}g을 ${date.slice(5).replace('-', '/')} ${mealNm}에 추가했어요.`, {
        type: 'success', action: { label: '식단 보기', onClick: () => navigate(`/diet/${date}`) },
      });
    } catch (e) {
      toast(e?.response?.data?.message || '추가 중 오류가 발생했습니다.', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fd">
      {category && <p className="fd-cat">{category}</p>}
      <h3 className="fd-sub">영양 성분 <small>100g 기준</small></h3>
      <dl className="fd-table">
        {DETAIL.map(([k, label, unit]) => (
          <div key={k}><dt>{label}</dt><dd>{fmt(food[k], 1)} {unit}</dd></div>
        ))}
      </dl>

      <h3 className="fd-sub">식단에 추가</h3>
      {authed ? (
        <div className="fd-add">
          <div className="fd-add-row">
            <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            <select className="select" value={meal} onChange={(e) => setMeal(e.target.value)}>
              {(mealCodes.length ? mealCodes : [{ code: 'BR', codeNm: '아침' }, { code: 'LC', codeNm: '점심' }, { code: 'DN', codeNm: '저녁' }, { code: 'SN', codeNm: '간식' }])
                .map((m) => <option key={m.code} value={m.code}>{m.codeNm}</option>)}
            </select>
            <label className="fd-g"><input className="input" type="number" min="1" value={intake} onChange={(e) => setIntake(e.target.value)} />g</label>
          </div>
          <div className="fd-add-sum">
            <span>{fmt(sc('kcal'))} kcal</span>
            <MacroText carbo={sc('carbo')} protein={sc('protein')} fat={sc('fat')} />
          </div>
          <button className="btn primary" type="button" disabled={!ok || !date || saving} onClick={add}>{saving ? '추가 중…' : '추가'}</button>
        </div>
      ) : (
        <button className="btn primary" type="button" onClick={() => navigate('/login')}>로그인하고 식단에 추가</button>
      )}
    </div>
  );
}
